/* FM-CF-V2 — cascading category tiles L1–L6 (chips + searchable fallback + breadcrumb)
 * Driver: this custom section IS the main category navigation. Native-backed:
 * child values come from Shopify faceting via the cf-facets-json section, scoped
 * to current selections. No new faceting engine. URL = source of truth via
 * filter.p.m.custom.category_level_1..6.
 *  - L1: large image tiles (unchanged)
 *  - L2..L6: uniform chips; >CHIP_THRESHOLD options => search input + scroll list
 *  - Breadcrumb above chips; clicking a crumb clears that level + all deeper
 *  - GRID_SECTION_ID derived dynamically (fixes the hardcoded-id 404)
 */
(function () {
  const DEBUG = false;
  const log = (...a) => DEBUG && console.log('[CF]', ...a);

  /* ===== Constants ===== */
  const WRAP_SEL         = '[data-cf-section], .cascading-filters';
  const LEVEL_SELECT_SEL = 'select[data-level][data-filter-param]';
  const JSON_SECTION_ID  = 'cf-facets-json';
  const CHIP_THRESHOLD   = 10; // <= threshold => plain chips; otherwise search + scroll list

  // Dynamic grid section id (fixes hardcoded GRID_SECTION_ID 404).
  function gridWrapperEl() {
    return document.querySelector('[id^="shopify-section-"][id$="__collection-products"]')
        || document.querySelector('[id^="shopify-section-"][id$="__main-collection-products"]');
  }
  function gridWrapperId() { const el = gridWrapperEl(); return el ? el.id : ''; }
  function gridSectionId() { return gridWrapperId().replace(/^shopify-section-/, ''); }

  const $  = (s, el = document) => el.querySelector(s);
  const $$ = (s, el = document) => Array.from(el.querySelectorAll(s));
  const collPath = () => location.pathname;
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, c => (
    { '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c]
  ));

  const PLACEHOLDERS = Array.isArray(window.CF_PLACEHOLDERS)
    ? window.CF_PLACEHOLDERS
    : ['All','Select previous first','Select previous first','Select previous first','Select previous first','Select previous first'];

  /* ===== State ===== */
  let CF_RESTORING = false;
  let CF_READY     = false;
  let REQ_TOKEN    = 0;
  let GRID_TOKEN   = 0;
  let RAF_REVEAL   = null;
  const CHANGE_DEBOUNCE_MS = 150;
  let changeTimer  = null;

  /* ===== Ordered selects ===== */
  function levelSelects() {
    return $$(LEVEL_SELECT_SEL).sort(
      (a, b) => (+a.getAttribute('data-level')) - (+b.getAttribute('data-level'))
    );
  }

  /* ===== URL helpers ===== */
  function getBaseParamsFromURL() {
    const keep = new URLSearchParams();
    const urlp = new URLSearchParams(location.search);
    ['sort_by','q','view','page'].forEach(k => { const v = urlp.get(k); if (v) keep.set(k, v); });
    return keep;
  }
  function getAllFilterParamsFromURL() {
    const urlp = new URLSearchParams(location.search);
    const out  = new URLSearchParams();
    urlp.forEach((v,k) => { if (k.startsWith('filter.')) out.set(k, v); });
    return out;
  }
  function buildParamsFromSelections() {
    const p = getBaseParamsFromURL();
    levelSelects().forEach(sel => {
      const key = sel.getAttribute('data-filter-param');
      const val = (sel.value || '').trim();
      if (key && val) p.set(key, val); else if (key) p.delete(key);
    });
    return p;
  }
  function setURL(params, { push = false } = {}) {
    if (CF_RESTORING || !CF_READY) { log('setURL skipped'); return; }
    const qs = params.toString();
    const href = qs ? `${collPath()}?${qs}` : collPath();
    push ? history.pushState({}, '', href) : history.replaceState({}, '', href);
  }

  /* ===== Facets JSON ===== */
  async function fetchFacetsJSON(params = new URLSearchParams()) {
    getBaseParamsFromURL().forEach((v,k)=>params.set(k,v));
    const url = `${collPath()}?section_id=${encodeURIComponent(JSON_SECTION_ID)}&${params.toString()}`;
    const html = await fetch(url, { credentials: 'same-origin' })
      .then(r => r.text())
      .catch(() => '');
    if (!html) return { filters: {} };
    const tpl = document.createElement('template');
    tpl.innerHTML = html.trim();
    const tag = tpl.content.querySelector('script[data-cf-json][type="application/json"]');
    try { return tag ? JSON.parse(tag.textContent.trim()) : { filters: {} }; }
    catch { return { filters: {} }; }
  }

  /* ===== Grid AJAX (token-guarded) ===== */
  async function fetchSectionHTML(sectionId, params) {
    const url = `${collPath()}?section_id=${encodeURIComponent(sectionId)}&${params.toString()}`;
    return fetch(url, { credentials: 'same-origin' }).then(r => r.text()).catch(()=> '');
  }
  async function updateGrid(params) {
    const SECTION_ID = gridSectionId();
    const WRAPPER_ID = gridWrapperId();
    if (!SECTION_ID || !WRAPPER_ID) { log('grid wrapper not found — skip'); return; }

    const keep = getBaseParamsFromURL();
    keep.forEach((v,k)=>params.set(k,v));

    const myToken = ++GRID_TOKEN;
    const current = document.getElementById(WRAPPER_ID);
    if (current) current.style.opacity = '0.5';

    const html = await fetchSectionHTML(SECTION_ID, params);
    if (myToken !== GRID_TOKEN) { log('stale grid ignored'); return; }

    const tpl  = document.createElement('template');
    tpl.innerHTML = html.trim();
    const incoming = tpl.content.querySelector(`#${CSS.escape(WRAPPER_ID)}`) || tpl.content.firstElementChild;
    if (!incoming) { if (current) current.style.opacity = ''; return; }

    if (current) current.replaceWith(incoming);
    else (document.querySelector('[data-products], .collection, .product-grid, main') || document.body).appendChild(incoming);
  }

  /* ===== Select + chip rendering ===== */
  const hasUsableOptions = (sel) => !!(sel && sel.options && sel.options.length > 1);

  function syncChipActive(sel) {
    const row = sel.closest('.cf-row');
    const root = row && row.querySelector('[data-cf-chips-root]');
    if (!root) return;
    const v = sel.value || '';
    root.querySelectorAll('.cf-chip').forEach(c => {
      c.classList.toggle('active', !!v && c.getAttribute('data-value') === v);
    });
  }

  function renderChips(sel) {
    const row = sel.closest('.cf-row');
    const root = row && row.querySelector('[data-cf-chips-root]');
    if (!root) return;
    const opts = Array.from(sel.options).filter(o => o.value);
    const cur  = sel.value || '';
    root.innerHTML = '';
    if (!opts.length) return;

    const makeChip = (o) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'cf-chip' + (o.value === cur ? ' active' : '');
      b.setAttribute('data-value', o.value);
      b.textContent = o.textContent;
      b.addEventListener('click', () => {
        sel.value = (sel.value === o.value) ? '' : o.value; // toggle
        if (CF_READY) sel.dispatchEvent(new Event('change', { bubbles: true }));
        syncChipActive(sel);
      });
      return b;
    };

    if (opts.length > CHIP_THRESHOLD) {
      const wrap = document.createElement('div'); wrap.className = 'cf-chips__search';
      const inp = document.createElement('input');
      inp.type = 'search'; inp.className = 'cf-chip-search';
      var __cfLbl = ((row.querySelector('.cf-label') || {}).textContent || '').trim().toLowerCase();
      inp.placeholder = __cfLbl ? ('Search ' + __cfLbl + '…') : 'Search…';
      const list = document.createElement('div'); list.className = 'cf-chips__list';
      opts.forEach(o => list.appendChild(makeChip(o)));
      inp.addEventListener('input', () => {
        const q = inp.value.toLowerCase();
        list.querySelectorAll('.cf-chip').forEach(c => {
          c.style.display = c.textContent.toLowerCase().includes(q) ? '' : 'none';
        });
      });
      wrap.appendChild(inp); wrap.appendChild(list); root.appendChild(wrap);
    } else {
      opts.forEach(o => root.appendChild(makeChip(o)));
    }
  }

  function fillSelect(selectEl, list, placeholder) {
    if (!selectEl) return false;
    const prev = (selectEl.value || '').trim();
    selectEl.innerHTML = '';

    const ph = document.createElement('option');
    ph.value = ''; ph.textContent = placeholder || 'Select...';
    selectEl.appendChild(ph);

    (list || []).forEach(item => {
      if (!item?.value) return;
      if (typeof item.count === 'number' && item.count <= 0) return;
      const opt = document.createElement('option');
      opt.value = item.value;
      opt.textContent = item.label || item.value;
      selectEl.appendChild(opt);
    });

    if (prev && !Array.from(selectEl.options).some(o => o.value === prev)) {
      const opt = document.createElement('option');
      opt.value = prev; opt.textContent = prev;
      selectEl.appendChild(opt);
    }

    selectEl.value = prev || '';
    const usable = hasUsableOptions(selectEl);
    selectEl.disabled = !usable;

    const row = selectEl.closest('.cf-row');
    const idx = parseInt(selectEl.getAttribute('data-level') || '0', 10);
    if (row && idx !== 0) row.classList.toggle('is-hidden', !usable);
    row?.classList.remove('is-loading');

    if (idx >= 1) renderChips(selectEl); // L1 keeps image tiles
    return usable;
  }

  /* ===== L1 tiles (unchanged behaviour) ===== */
  function readTilesConfigFromJSON(scope) {
    try {
      const tag = (scope || document).querySelector('script[data-cf-l1-tiles]') || document.querySelector('script[data-cf-l1-tiles]');
      if (!tag) return null;
      const json = JSON.parse(tag.textContent || '{}');
      return Array.isArray(json.tiles) && json.tiles.length ? json.tiles : null;
    } catch { return null; }
  }
  function readTilesFromSelect(l1) {
    const out = [];
    if (!l1) return out;
    Array.from(l1.options || []).forEach(opt => {
      const v = (opt.value || '').trim();
      if (!v) return;
      out.push({ value: v, label: (opt.text || v).trim(), image: null });
    });
    return out;
  }
  function renderL1Tiles(scope, l1, preList) {
    if (!l1) return;
    const root = (scope || document).querySelector('[data-cf-l1-root]') || document.querySelector('[data-cf-l1-root]');
    if (!root) return;

    let tiles =
      readTilesConfigFromJSON(scope)
      || (Array.isArray(preList) && preList.length ? preList.map(i => ({ value: i.value, label: i.label || i.value, image: i.image })) : null)
      || readTilesFromSelect(l1);

    if (!tiles || tiles.length <= 1) { root.innerHTML = ''; return; }

    root.innerHTML = tiles.map((t, i) => {
      const img = t.image ? `<img src="${esc(t.image)}" alt="">` : '';
      const lbl = esc(t.label || t.value);
      return `<button type="button" class="cf-tile" data-value="${encodeURIComponent(t.value)}" style="animation-delay:${i*40}ms">${img}<span class="cf-tile__text">${lbl}</span></button>`;
    }).join('');

    const syncActive = () => {
      const v = l1.value || '';
      root.querySelectorAll('.cf-tile').forEach(btn => {
        const val = decodeURIComponent(btn.getAttribute('data-value') || '');
        btn.classList.toggle('active', !!v && v === val);
      });
    };
    syncActive();

    root.querySelectorAll('.cf-tile').forEach(btn => {
      btn.addEventListener('click', () => {
        const val = decodeURIComponent(btn.getAttribute('data-value') || '');
        l1.value = (l1.value === val) ? '' : val;
        if (CF_READY) l1.dispatchEvent(new Event('change', { bubbles: true }));
        syncActive();
      });
    });
    l1.addEventListener('change', syncActive);
  }
  async function ensureL1Tiles({ tries = 30, everyMs = 120 } = {}) {
    const scope = $(WRAP_SEL) || document;
    for (let n = 0; n < tries; n++) {
      const l1 = $('select[data-level="0"]');
      const mount = scope.querySelector('[data-cf-l1-root]') || document.querySelector('[data-cf-l1-root]');
      if (l1 && mount) {
        let preList = [];
        if (!(l1.options && l1.options.length > 1)) {
          const json0 = await fetchFacetsJSON();
          const k = l1.getAttribute('data-filter-param') || '';
          preList = (json0.filters && json0.filters[k]) || [];
          if (preList.length) fillSelect(l1, preList, PLACEHOLDERS[0] || 'All');
        }
        renderL1Tiles(scope, l1, preList);
        return;
      }
      await new Promise(r => setTimeout(r, everyMs));
    }
    log('ensureL1Tiles: L1 or mount not found');
  }

  /* ===== Breadcrumb ===== */
  function renderBreadcrumb() {
    const nav = $('[data-cf-breadcrumb]');
    if (!nav) return;
    const crumbs = [];
    levelSelects().forEach(sel => {
      const v = (sel.value || '').trim();
      if (!v) return;
      const idx = +sel.getAttribute('data-level');
      const opt = Array.from(sel.options).find(o => o.value === v);
      crumbs.push({ idx, label: opt ? opt.textContent : v });
    });
    if (!crumbs.length) { nav.innerHTML = ''; return; }
    nav.innerHTML = crumbs.map((c, i) => (
      (i > 0 ? '<span class="cf-breadcrumb__sep" aria-hidden="true">›</span>' : '') +
      `<button type="button" class="cf-breadcrumb__crumb" data-cat-level="${c.idx}">` +
        `<span class="cf-breadcrumb__label">${esc(c.label)}</span>` +
        `<span class="cf-breadcrumb__x" aria-hidden="true">×</span>` +
      `</button>`
    )).join('') + '<button type="button" class="cf-breadcrumb__clear">Clear path</button>';
  }
  function wireBreadcrumb() {
    const nav = $('[data-cf-breadcrumb]');
    if (!nav || nav.__cfWired) return;
    nav.__cfWired = true;
    nav.addEventListener('click', (e) => {
      if (e.target.closest('.cf-breadcrumb__clear')) { e.preventDefault(); if (window.CF_resetAll) window.CF_resetAll(); return; }
      const btn = e.target.closest('.cf-breadcrumb__crumb');
      if (!btn) return;
      const lvl = parseInt(btn.getAttribute('data-cat-level'), 10);
      const sel = levelSelects().find(s => +s.getAttribute('data-level') === lvl);
      if (!sel) return;
      sel.value = ''; // clears this level; updateDownstream clears all deeper
      if (CF_READY) sel.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function updateL1Mode() {
    const wrap = $(WRAP_SEL); if (!wrap) return;
    const l1 = $('select[data-level="0"]');
    wrap.classList.toggle('cf--l1-collapsed', !!(l1 && l1.value));
  }

  /* ===== Reveal loop (generalised to N levels) ===== */
  function scheduleReveal() {
    if (RAF_REVEAL) return;
    RAF_REVEAL = requestAnimationFrame(() => {
      RAF_REVEAL = null;
      updateL1Mode();
      const selects = levelSelects();
      let parentChosen = true; // L1 row always visible
      for (let i = 0; i < selects.length; i++) {
        const sel = selects[i];
        const idx = +sel.getAttribute('data-level');
        const row = sel.closest('.cf-row');
        if (idx === 0) { parentChosen = !!sel.value; continue; }
        const usable = hasUsableOptions(sel) && !sel.disabled;
        if (parentChosen && usable) row?.classList.remove('is-hidden');
        else row?.classList.add('is-hidden');
        parentChosen = parentChosen && !!sel.value && usable;
      }
    });
  }

  /* ===== Downstream (race-safe) ===== */
  async function updateDownstream(levelIdxChanged) {
    if (CF_RESTORING || !CF_READY) return;
    const selects = levelSelects();
    const token   = ++REQ_TOKEN;

    // Value removed at this level -> clear + hide all deeper; back to page 1
    if (!selects[levelIdxChanged]?.value) {
      for (let i = levelIdxChanged + 1; i < selects.length; i++) {
        selects[i].closest('.cf-row')?.classList.add('is-hidden');
        fillSelect(selects[i], [], PLACEHOLDERS[i] || 'Select previous first');
      }
      const p0 = buildParamsFromSelections();
      p0.delete('page');
      setURL(p0);
      renderBreadcrumb();
      scheduleReveal();
      await updateGrid(p0);
      return;
    }

    // Loading state on deeper levels
    for (let i = levelIdxChanged + 1; i < selects.length; i++) {
      const sel = selects[i];
      const row = sel.closest('.cf-row'); row?.classList.add('is-hidden'); row?.classList.add('is-loading');
      sel.innerHTML = `<option value="">Loading...</option>`;
      sel.disabled = true;
    }
    scheduleReveal();

    const params = buildParamsFromSelections();
    params.delete('page');
    const [json] = await Promise.all([ fetchFacetsJSON(params), updateGrid(params) ]);
    if (token !== REQ_TOKEN) return;

    for (let i = levelIdxChanged + 1; i < selects.length; i++) {
      const sel  = selects[i];
      const key  = sel.getAttribute('data-filter-param') || '';
      const list = (json.filters && json.filters[key]) || [];
      const has  = fillSelect(sel, list, PLACEHOLDERS[i] || 'Select...');
      if (!has) {
        for (let j = i + 1; j < selects.length; j++) {
          selects[j].closest('.cf-row')?.classList.add('is-hidden');
          fillSelect(selects[j], [], PLACEHOLDERS[j] || 'Select previous first');
        }
        break;
      }
    }

    setURL(params);
    renderBreadcrumb();
    scheduleReveal();
  }

  /* ===== Bootstrap ===== */
  async function bootstrap() {
    CF_RESTORING = true; CF_READY = false;

    const selects = levelSelects();
    const l1 = selects[0];

    const json0    = await fetchFacetsJSON();
    const firstKey = l1?.getAttribute('data-filter-param') || '';
    const l1List   = (json0.filters && json0.filters[firstKey]) || [];
    fillSelect(l1, l1List, PLACEHOLDERS[0] || 'All');
    l1?.closest('.cf-row')?.classList.remove('is-hidden');

    const urlParams = new URLSearchParams(location.search);
    if (urlParams.size) {
      selects.forEach(sel => {
        const key = sel.getAttribute('data-filter-param') || '';
        const v   = key ? urlParams.get(key) : '';
        if (v) sel.value = v;
      });
    }

    await ensureL1Tiles();

    for (let i = 0; i < selects.length - 1; i++) {
      const p = buildParamsFromSelections();
      const json = await fetchFacetsJSON(p);
      const nextSel = selects[i + 1]; if (!nextSel) continue;
      const key = nextSel.getAttribute('data-filter-param') || '';
      const list = (json.filters && json.filters[key]) || [];
      fillSelect(nextSel, list, PLACEHOLDERS[i + 1] || 'Select previous first');
    }

    renderBreadcrumb();
    scheduleReveal();
    await updateGrid(buildParamsFromSelections());

    CF_RESTORING = false; CF_READY = true;
  }

  /* ===== Pagination intercept ===== */
  function interceptPaginationClicks() {
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a'); if (!a) return;
      if (a.target === '_blank' || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const WRAPPER_ID = gridWrapperId(); if (!WRAPPER_ID) return;
      const inGrid = a.closest(`#${CSS.escape(WRAPPER_ID)}`); if (!inGrid) return;
      const url = new URL(a.href, location.origin);
      if (url.pathname !== location.pathname) return;
      const nextPage = url.searchParams.get('page'); if (!nextPage) return;
      e.preventDefault();
      const params  = getBaseParamsFromURL();
      const filters = getAllFilterParamsFromURL();
      filters.forEach((v, k) => params.set(k, v));
      params.set('page', nextPage);
      setURL(params, { push: true });
      updateGrid(params);
    });
  }

  /* ===== Events + Reset ===== */
  function wire() {
    $$(LEVEL_SELECT_SEL).forEach(sel => {
      sel.addEventListener('change', () => {
        if (CF_RESTORING || !CF_READY) return;
        clearTimeout(changeTimer);
        changeTimer = setTimeout(() => {
          const idx = parseInt(sel.getAttribute('data-level') || '0', 10);
          updateDownstream(idx).catch(console.error);
        }, CHANGE_DEBOUNCE_MS);
      });
    });

    wireBreadcrumb();
    window.addEventListener('popstate', () => { bootstrap().catch(console.error); });
    interceptPaginationClicks();

    window.CF_resetAll = async () => {
      const wrap = $(WRAP_SEL) || document;
      const selects = levelSelects();
      const l1 = selects[0];
      wrap?.querySelectorAll('[data-cf-l1-root] .cf-tile').forEach(t => t.classList.remove('active'));

      const was = CF_RESTORING; CF_RESTORING = true;

      if (l1) {
        const json0    = await fetchFacetsJSON(new URLSearchParams());
        const firstKey = l1.getAttribute('data-filter-param') || '';
        const l1List   = (json0.filters && json0.filters[firstKey]) || [];
        fillSelect(l1, l1List, PLACEHOLDERS[0] || 'All');
        l1.value = '';
        l1.disabled = !hasUsableOptions(l1);
        l1.closest('.cf-row')?.classList.remove('is-hidden');
        renderL1Tiles(wrap, l1, l1List);
      }

      selects.slice(1).forEach((sel, i) => {
        fillSelect(sel, [], PLACEHOLDERS[i + 1] || 'Select previous first');
        sel.disabled = true;
        sel.closest('.cf-row')?.classList.add('is-hidden');
      });

      const p = new URLSearchParams();
      const cur = new URLSearchParams(location.search);
      ['q','view'].forEach(k => { const v = cur.get(k); if (v) p.set(k, v); });

      CF_RESTORING = false;
      setURL(p);
      renderBreadcrumb();
      scheduleReveal();
      await updateGrid(p);
      CF_RESTORING = was;
    };
  }

  document.addEventListener('DOMContentLoaded', () => {
    bootstrap().then(wire).catch(console.error);
  });
})();
